import type { CSSProperties } from 'react';

/**
 * Flatten a React Native style prop into the plain object a raw DOM
 * element accepts.
 *
 * The `.web.tsx` Gluestack primitives (VStack, HStack, Box, ...) render a
 * bare `<div>` and pass `style` straight through. React Native code is free
 * to hand them an array, and React Native Web does so itself:
 * `VirtualizedList` clones `ListEmptyComponent` with
 * `style={[inversionStyle, element.props.style]}`. React DOM then assigns
 * `style[0]`, which throws "Failed to set an indexed property [0] on
 * 'CSSStyleDeclaration'" and takes the whole route down. That is how the
 * mobile web directory crashed on load while its list was empty.
 *
 * Later entries win, and falsy entries are skipped, as in
 * `StyleSheet.flatten`. Plain objects pass through untouched.
 */
export function toDomStyle(style: unknown): CSSProperties | undefined {
  if (!Array.isArray(style)) {
    return (style || undefined) as CSSProperties | undefined;
  }

  const flat: Record<string, unknown> = {};

  for (const entry of style) {
    Object.assign(flat, toDomStyle(entry));
  }

  return Object.keys(flat).length > 0 ? (flat as CSSProperties) : undefined;
}
